import { CITY } from '@city'
import { useSyncExternalStore, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StationCard, type StationCardCard } from '@metro/station-card'
import { RouteCard } from '@metro/route-card'
import { buildRouteCardCopy, buildStationCardCopy } from '../i18n/packageStrings'
import { Suggester } from '@metro/suggester'
import type { StationStatusSnapshot } from '@metro/station-status'
import { StationDetailsWithAnalytics } from './StationDetailsWithAnalytics'
import { MobileRouteDetailsOverlay } from './MobileRouteDetailsOverlay'
import { useSuggesterPanel } from '../state/useSuggesterPanel'
import { useRouteDetailsPayload } from '../state/useRouteDetailsPayload'
import {
  noopMetroAnalyticsReporter,
  type MetroAnalyticsReporter,
} from '../interaction/metroAnalyticsReporter'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'
import type { SchemaData } from '../schema/types'
import { lineHexColorFromId, resolveStationById } from '../interaction/stationResolver'
import { useRouteSelection } from '../state/useRouteSelection'
import { useMetroMapDispatch, useMetroMapStore } from '../state/useMetroMapStore'
import { effectiveTheme } from '../state/effectiveTheme'
import { useRoutingAdapter } from '../state/useRoutingAdapter'
import { useRouteVariantTappedReporter } from '../state/useRouteVariantTappedReporter'
import { useRouteCardEdits } from '../state/useRouteCardEdits'
import { useFeedback } from '../state/useFeedback'
import { useRouteHistoryRecorder } from '../state/routeLibrary/useRouteHistoryRecorder'
import { routeTimesClock } from '../state/routeTimesClock'
import { detailsTimetable } from './detailsTimetable'
import styles from './StationCardOrDetailsRouter.module.css'
import { LineViewHost } from '../lineView/LineViewHost'
import { BREAKPOINT_PX } from './breakpoint'
import { useLineLinks, type LineLinks } from '../lineView/useLineLinks'

export { BREAKPOINT_PX }

export interface StationCardOrDetailsRouterProps {
  selectedStation: SelectedStationSnapshot | null
  schema: SchemaData
  cardsForCarousel: ReadonlyArray<StationCardCard>
  /**
   * Transfers for the currently-selected station, resolved from the
   * schema by the host. Forwarded into <StationDetails> on the
   * expanded surface. The compact <StationCard> no longer renders
   * the transfers list inline — those will live in the (future) station
   * details route reachable via the card's "Station information" action.
   */
  transfers?: ReadonlyArray<{
    id: string
    stationName: string
    lineLocalName: string
    lineHexColor: string
  }>
  onSettled: (id: string) => void
  onTransferTap: (id: string) => void
  onFavoriteTap: () => void
  onPaywallTap: () => void
  onReportIssueTap: () => void
  /**
   * Live favourited-station ids from the shared FavoritesService
   * (spec §16), threaded from AppShell's `useSyncExternalStore`
   * subscription. The expanded StationDetails mount derives its
   * `isFavorite` from CLUSTER (`naptanId`) membership, so the heart reflects — and re-renders
   * with — the same singleton Suggester's idle Favorites section reads.
   */
  favoriteIds: ReadonlyArray<string>
  /**
   * Project analytics reporter. Forwarded into
   * {@link StationDetailsWithAnalytics} on the expanded sidebar mount
   * so the §8 events emit at runtime. AppShell injects
   * `realMetroAnalyticsReporter` (forwards to GA4 +
   * Amplitude). Optional — defaults to the no-op reporter so direct
   * unit-test renders stay silent.
   */
  reporter?: MetroAnalyticsReporter
  /**
   * Card-action callbacks for the compact carousel (per iOS parity:
   * From / To / Station information). `onCardFromTap` /
   * `onCardToTap` are wired in `StationDetailHost` to dispatch
   * `RouteFromSelected` / `RouteToSelected` and dismiss the carousel
   * (Task 18).
   */
  onCardFromTap?: (stationId: string) => void
  onCardToTap?: (stationId: string) => void
  onCardDetailsTap?: (stationId: string) => void
  /**
   * Live status snapshot (naptanId-keyed) from StationStatusController.
   * Forwarded to StationCard so each card resolves its own banner
   * (spec §6). Defaults to empty (all Open).
   */
  statuses?: StationStatusSnapshot
  /**
   * Fired when the user taps the status banner on a card.
   * Routes to DisruptionDetails (spec §6).
   */
  onStatusBannerTap?: (stationId: string) => void
}

/**
 * Viewport-branched router. Mounts <StationDetails> at >= 768px (tablet
 * + desktop), <StationCard> at < 768px (phone). Reactive to viewport
 * resize via window.matchMedia + useSyncExternalStore.
 *
 * Resolves the station record by id from the schema. Returns null when no
 * station is selected, or when the selected id is not in the schema (a
 * transient state that can occur during schema load / drift).
 */
export function StationCardOrDetailsRouter(
  props: StationCardOrDetailsRouterProps,
): React.ReactElement | null {
  const { t: tRouteCard } = useTranslation('route-card')
  const routeCardCopy = useMemo(() => buildRouteCardCopy(tRouteCard), [tRouteCard])
  const { t: tStationCard } = useTranslation('station-card')
  const stationCardCopy = useMemo(() => buildStationCardCopy(tStationCard), [tStationCard])
  const isExpanded = useSyncExternalStore(
    (callback) => {
      const mql = window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`)
      mql.addEventListener('change', callback)
      return () => mql.removeEventListener('change', callback)
    },
    () => window.matchMedia(`(min-width: ${BREAKPOINT_PX}px)`).matches,
    () => false, // SSR fallback (not used since metro-frontend is CSR)
  )

  // Hooks must run unconditionally — these consume contexts mounted by
  // <AppShell>'s <MetroMapStoreProvider> (a single store-slice
  // subscription each).
  const routeSelection = useRouteSelection()
  const dispatch = useMetroMapDispatch()
  const adapter = useRoutingAdapter()
  const reportVariantTapped = useRouteVariantTappedReporter()
  const routeCardEdits = useRouteCardEdits()
  const store = useMetroMapStore()
  const routeStatus = useSyncExternalStore(store.subscribe, () => store.getState().routeStatus)
  // The EFFECTIVE theme — the signal that picks the map's light/dark
  // MMTG geometry, night-tube included (2026-07 color contract).
  const theme = useSyncExternalStore(store.subscribe, () => effectiveTheme(store.getState()))

  // Suggester picker wiring (TASK-412) — shared with the phone overlay
  // so the desktop and phone surfaces never drift (spec §5). Reads the
  // `?suggester=` slot param; `panel.slot` is non-null while the picker
  // is open in this panel.
  const suggester = useSuggesterPanel()

  // The line link under the station name (Figma LineView E) on both the
  // phone card and StationDetails.
  const lineLinks = useLineLinks()

  // RouteDetails payload (TASK-412) — same shared derivation the phone
  // overlay uses. `details.canOpen` is true exactly while the route is in
  // the results lifecycle with a selected route and the adapter loaded.
  //
  // TASK-412 #4/#5: on the tablet/desktop side panel RouteDetails is
  // shown INLINE and AUTOMATICALLY (list-on-top / detail-below), driven
  // purely by `details.canOpen` — there is NO "Route Details" button and
  // NO local `detailsOpen` flag here (the phone modal keeps both). Because
  // visibility is derived from `canOpen`, clearing the route (routeStatus
  // → collapsed / empty) drops `canOpen` to false and the inline detail
  // disappears on the next render with no effect cascade (#5). The detail
  // follows the selected variant automatically: `details.selectedRoute`
  // tracks `routeStatus.selectedIndex`, which a list tap updates.
  const details = useRouteDetailsPayload()

  // I (Feedback): RouteDetails's ReportIssueRow (inline in the side
  // panel) opens the Feedback form with the `routeDetails` context.
  const { openFeedback } = useFeedback()

  const stationData = useMemo(() => {
    if (props.selectedStation === null) return null
    const found = props.schema.stations.find(
      (st: { id: string }) => st.id === props.selectedStation!.id,
    )
    return found ?? null
  }, [props.selectedStation, props.schema])

  const haveStation = props.selectedStation !== null && stationData !== null

  // The one condition under which this surface shows RouteDetails (see the
  // JSX below for what each clause means). Hoisted above the viewport branch
  // because hooks must run unconditionally, and reused verbatim in the JSX
  // so the render and the history write can never disagree about whether the
  // rider is looking at a route.
  const routeDetailsVisible =
    isExpanded &&
    suggester.slot === null &&
    !haveStation &&
    details.canOpen &&
    details.selectedRoute !== null &&
    details.lineLookup !== null

  // TASK-733: the desktop half of the single route-library write point —
  // the phone modal (`MobileRouteCardOverlay`) calls the SAME hook with its
  // own open condition. `SelectedStationSnapshot` carries an `id`, not a
  // `slug`; the route key is keyed on the rename-stable cluster slug, so
  // both ends are resolved through the schema.
  useRouteHistoryRecorder({
    isOpen: routeDetailsVisible,
    fromSlug:
      resolveStationById(props.schema, routeSelection.fromStation?.id ?? '')?.station.slug ?? null,
    toSlug:
      resolveStationById(props.schema, routeSelection.toStation?.id ?? '')?.station.slug ?? null,
    route: details.selectedRoute,
    reporter: props.reporter ?? noopMetroAnalyticsReporter,
  })

  // -- Expanded (>= 768px) viewport -----------------------------------
  if (isExpanded) {
    // SelectedStationSnapshot does not carry `lineHexColor` (it is a
    // shape-stable identifier, not a render-time payload). Resolve via
    // the schema; missing entries are tolerated as `null` and surface
    // as the empty placeholder state on the destination button.
    const fromLineColor =
      routeSelection.fromStation === null
        ? null
        : lineHexColorFromId(props.schema, routeSelection.fromStation.lineId, theme)
    const toLineColor =
      routeSelection.toStation === null
        ? null
        : lineHexColorFromId(props.schema, routeSelection.toStation.lineId, theme)

    return (
      <div className={styles.expandedSidebar} data-testid="station-details-shell">
        <RouteCard
          variant="tablet"
          copy={routeCardCopy}
          from={
            routeSelection.fromStation === null || fromLineColor === null
              ? null
              : {
                  id: routeSelection.fromStation.id,
                  shortLocalName: routeSelection.fromStation.localName,
                  lineHexColor: fromLineColor,
                }
          }
          to={
            routeSelection.toStation === null || toLineColor === null
              ? null
              : {
                  id: routeSelection.toStation.id,
                  shortLocalName: routeSelection.toStation.localName,
                  lineHexColor: toLineColor,
                }
          }
          onFromTap={() => suggester.open('from')}
          onToTap={() => suggester.open('to')}
          onFromClear={routeCardEdits.onFromClear}
          onToClear={routeCardEdits.onToClear}
          onSwap={routeCardEdits.onSwap}
          // TASK-391 §3.3 — on tablet/desktop (regular width) the my-location
          // button is NOT in the side panel; it floats over the map
          // bottom-right (see <MyLocationFloatingButton> in AppShell,
          // 2026-06-18). So `onMyLocation` is intentionally omitted here. The
          // phone (compact) overlay keeps it in the collapsed card.
          // Pills-as-search (TASK-412): the open suggester slot's pill
          // becomes the query input; typed text drives the suggester
          // query (the host owns it in `useSuggesterPanel`).
          activeSearchSlot={suggester.slot}
          searchQuery={suggester.query}
          onSearchQueryChange={suggester.setQuery}
          routeStatus={routeStatus}
          lineLookup={adapter?.lineLookupFor(theme)}
          clock={routeTimesClock(routeStatus)}
          // TASK-412 #4: NO "Route Details" button on the tablet/desktop
          // side panel — RouteCard hides it for `variant="tablet"` and
          // the host renders RouteDetails inline below (auto on variant
          // select). `onOpenRouteDetails` is intentionally omitted here.
          onSelectVariant={(idx) => {
            dispatch({ kind: 'RouteSelectedVariantChanged', index: idx })
            reportVariantTapped(idx)
          }}
        />
        {/* Suggester picker — opened in-panel by a From/To pill tap
            (TASK-412, fixes the desktop-unreachable gap). The pills above
            stay as persistent top chrome; the picker (its own search
            field + section list) renders below as the route-build body
            region. Mutually exclusive with StationDetails within the
            route-building region (spec §4). `layout="sidePanel"` gives
            the content-only chrome — the host card supplies the glass. */}
        {/* TASK-412 U+V — `key` on the slot so each present is a fresh
            instance: Suggester owns its store + the one-shot mount-time
            permission probe, so a re-present (close→reopen, from↔to switch)
            must REMOUNT to re-run the probe / Nearby auto-resolve instead of
            reusing the latched instance (the iOS U+V "banner stuck + tap
            no-ops on relaunch" bug). Mirrors iOS keying `.task` on the store
            identity. */}
        {suggester.slot !== null && (
          <Suggester key={suggester.slot} layout="sidePanel" {...suggester.props(suggester.slot)} />
        )}
        {/* RouteDetails INLINE in the panel (TASK-412 #4 — list on top,
            detail below). AUTO-shown whenever the route is in the results
            lifecycle with a selected route (`details.canOpen`); there is
            NO button and NO open/close flag. It follows the selected
            variant: tapping a row in the list above dispatches
            RouteSelectedVariantChanged, which updates
            `details.selectedRoute`, which re-renders this detail (#4).
            Clearing the route (routeStatus → collapsed / empty) flips
            `details.canOpen` to false, so this unmounts automatically and
            the panel falls back to the empty RouteCard builder (#5).
            Suppressed while the suggester owns the region, and while a
            station is selected (§13 — StationDetails supersedes).
            `layout="sidePanel"` → content-only chrome; the host card is
            the surface. */}
        {/* `routeDetailsVisible` IS the gate (hoisted above the viewport
            branch); the two null checks that follow are the same clauses
            repeated only so TypeScript narrows the props below. */}
        {routeDetailsVisible && details.selectedRoute !== null && details.lineLookup !== null && (
          <MobileRouteDetailsOverlay
            layout="sidePanel"
            rawRoutes={details.rawRoutes}
            selectedRoute={details.selectedRoute}
            // Web is 100% free (no paywall) — every user gets premium
            // content, so `isPremium` is hard-`true`. This suppresses the
            // S5 upsell banner and renders the S4 convenient-car strip.
            isPremium={true}
            lineLookup={details.lineLookup}
            resolveStation={details.resolveStation}
            possibleTransfersByLeg={details.possibleTransfersByLeg}
            platformBoundsByLeg={details.platformBoundsByLeg}
            // Ride-leg indices that render the striped connector (DLR /
            // Overground) — matches the map's dashed lines.
            outlinedLegs={details.outlinedLegs}
            // Inline, derived view — no dismiss affordance. Escape must
            // not tear down the auto-detail (the route is still active);
            // it disappears only when the route itself is cleared (#5).
            onClose={() => {}}
            onSelectRoute={(route) => {
              const idx = details.rawRoutes.findIndex((r) => r.id === route.id)
              if (idx >= 0) dispatch({ kind: 'RouteSelectedVariantChanged', index: idx })
            }}
            onOpenTransferPaywall={() => {
              // Web has no paywall surface yet — no-op (analytics still
              // fires inside the overlay). Mirrors the phone overlay.
            }}
            onReportIssue={() => openFeedback('routeDetails')}
            clock={routeTimesClock(routeStatus)}
          />
        )}
        {/* StationDetails in the panel. Renders whenever a station is
            selected and the suggester does not own the region (spec §4
            precedence: StationDetails supersedes RouteDetails). It is
            NOT gated on `routeStatus === collapsed`: §13 allows a map
            station-tap to open StationDetails even over a built route
            (the tap carries `dismissSuggester`, so the reducer's
            route-active block is bypassed and `selectedStation` is set).
            Outside the §13 path `selectedStation` is null during results
            (map taps are blocked), so this only fires for an explicit
            station selection — matching iOS/Android. */}
        {haveStation &&
          suggester.slot === null &&
          renderStationDetails(stationData, props, lineLinks)}
        {/* Line view (`/<locale>/lines/<slug>`) — below the route builder,
            in the region StationDetails would use. LineViewHost renders only
            while the line owns the surface (no station, no route); the
            suggester still wins the region. */}
        {suggester.slot === null && <LineViewHost surface="panel" />}
      </div>
    )
  }

  // -- Compact (< 768px) viewport — unchanged in this task ------------
  if (props.selectedStation === null || stationData === null) return null

  const noop = (_id: string) => {
    /* placeholder until route-builder/details intents land */
  }
  const lineIdOf = (stationId: string): string | undefined =>
    props.schema.stations.find((st) => st.id === stationId)?.line.id
  const cards = props.cardsForCarousel.map((card) => {
    const lineId = lineIdOf(card.id)
    const lineHref = lineId === undefined ? undefined : lineLinks.hrefFor(lineId)
    // A linked line name reads like its view's title ("Bakerloo line ›").
    return lineHref === undefined
      ? card
      : { ...card, lineHref, lineLocalName: lineLinks.titleFor(lineId!) ?? card.lineLocalName }
  })
  return (
    <StationCard
      copy={stationCardCopy}
      cards={cards}
      onLineTap={(stationId) => {
        const lineId = lineIdOf(stationId)
        if (lineId !== undefined) lineLinks.open(lineId, 'station_card')
      }}
      currentId={props.selectedStation.id}
      onSettled={props.onSettled}
      onFromTap={props.onCardFromTap ?? noop}
      onToTap={props.onCardToTap ?? noop}
      onDetailsTap={props.onCardDetailsTap ?? noop}
      showRouteButtons
      // Live status snapshot — drives Closed / Restricted / Advisory banners
      // per spec §6. Banner tap routes to DisruptionDetails.
      statuses={props.statuses}
      onStatusBannerTap={props.onStatusBannerTap}
      onNotFoundRetryTap={undefined}
    />
  )
}

/**
 * Extracted to keep the conditional-render block readable. All props
 * forwarded into <StationDetails> match the previous inline call
 * exactly — no behaviour change for the legacy code path.
 */
function renderStationDetails(
  stationData: SchemaData['stations'][number],
  props: StationCardOrDetailsRouterProps,
  lineLinks: LineLinks,
): React.ReactElement {
  return (
    <StationDetailsWithAnalytics
      stationId={stationData.id}
      stationName={stationData.localName}
      lineLocalName={
        (lineLinks.hrefFor(stationData.line.id) === undefined
          ? undefined
          : lineLinks.titleFor(stationData.line.id)) ??
        stationData.line.localName ??
        ''
      }
      lineHexColor={stationData.line.hexColor}
      lineHref={lineLinks.hrefFor(stationData.line.id)}
      onLineTap={() => lineLinks.open(stationData.line.id, 'station_details')}
      // Figma SD/StationDetailsLink (9206:60036): the station's own page.
      // The page resolves a NaPTAN id, a slug or a schema id; the NaPTAN id
      // is the canonical one (`/en/station/940GZZLUBST`).
      stationPageHref={
        CITY.stationPages
          ? `${lineLinks.localePath}/station/${encodeURIComponent(stationData.naptanId ?? stationData.id)}`
          : undefined
      }
      onStationPageTap={() =>
        (props.reporter ?? noopMetroAnalyticsReporter)('station_page_link_tapped', {
          station_id: stationData.id,
        })
      }
      zone={stationData.zone ?? null}
      lat={stationData.coordinate?.latitude ?? 0}
      lng={stationData.coordinate?.longitude ?? 0}
      facilities={stationData.facilities ?? []}
      timetable={detailsTimetable(stationData.stationTimetable)}
      transfers={props.transfers ?? []}
      // Live favourite state from the shared FavoritesService (spec §16),
      // threaded through by the host. The store is keyed by CLUSTER
      // (`naptanId`), not by schema record — King's Cross is several
      // records, one favourite. A station without a naptan is simply never
      // favourited. Mirrors the phone modal path in AppShell.
      isFavorite={
        stationData.naptanId !== undefined && props.favoriteIds.includes(stationData.naptanId)
      }
      // Web is 100% free (no paywall, product contract §0.4) — every user
      // gets premium content, so `isPremium` is hard-`true`: the
      // TimetableUpsellBanner never renders and the timetable / premium
      // sections show free. `onPaywallTap` stays wired for cross-platform
      // analytics parity but is now unreachable on web (no upsell surface);
      // it re-activates only if web ever gains a paywall.
      isPremium={true}
      isOffline={false}
      onTransferTap={props.onTransferTap}
      onFavoriteTap={props.onFavoriteTap}
      onPaywallTap={props.onPaywallTap}
      onReportIssueTap={props.onReportIssueTap}
      onRetry={() => {}}
      reporter={props.reporter ?? noopMetroAnalyticsReporter}
    />
  )
}
